/**
 * Retrieval progress SSE context
 *
 * Provides connectRefresh (for FULL_ORG_REFRESH / METADATA_TYPE) and
 * connectInitial (for INITIAL_RETRIEVAL) using @microsoft/fetch-event-source.
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import {
  createRetrievalRefreshConnection,
  createRetrievalInitialConnection,
  type RetrievalConnectionHandlers,
} from './connection';
import type { RetrievalSSEEventType } from './connection';
import type { RefreshProgressData, InitialProgressData, RetrievalCompleteData, RetrievalRefreshErrorData } from './types';

// Refresh: progress_update and complete payloads
type RefreshData = RefreshProgressData | RetrievalCompleteData;
// Initial: progress_update, phase_transition, complete
type InitialData = InitialProgressData | RetrievalCompleteData | { from_phase?: string; to_phase?: string; timestamp?: string; retrieval_id?: string };

export interface RetrievalRefreshHandlers {
  onEvent?: (event: RetrievalSSEEventType, data: RefreshData) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;
  onUnauthenticated?: (data: RetrievalRefreshErrorData) => void;
}

export interface RetrievalInitialHandlers {
  onEvent?: (event: RetrievalSSEEventType, data: InitialData) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;
}

export interface RetrievalProgressContextValue {
  /** Subscribe to refresh stream (/retrievals/:id/refresh). Returns unsubscribe. */
  connectRefresh: (retrievalId: string, handlers: RetrievalRefreshHandlers) => () => void;
  /** Subscribe to initial retrieval stream (/retrievals/:id/progress). Returns unsubscribe. */
  connectInitial: (retrievalId: string, handlers: RetrievalInitialHandlers) => () => void;
}

const RetrievalProgressContext = createContext<RetrievalProgressContextValue | null>(null);

export interface RetrievalProgressProviderProps {
  children: ReactNode;
}

export function RetrievalProgressProvider({ children }: RetrievalProgressProviderProps) {
  const connectRefresh = useCallback((retrievalId: string, handlers: RetrievalRefreshHandlers) => {
    const conn = createRetrievalRefreshConnection(retrievalId, handlers as RetrievalConnectionHandlers<RefreshData>);
    return () => conn.abort();
  }, []);

  const connectInitial = useCallback((retrievalId: string, handlers: RetrievalInitialHandlers) => {
    const conn = createRetrievalInitialConnection(retrievalId, handlers as RetrievalConnectionHandlers<InitialData>);
    return () => conn.abort();
  }, []);

  const value = useMemo<RetrievalProgressContextValue>(
    () => ({ connectRefresh, connectInitial }),
    [connectRefresh, connectInitial]
  );

  return (
    <RetrievalProgressContext.Provider value={value}>
      {children}
    </RetrievalProgressContext.Provider>
  );
}

export function useRetrievalProgressContext(): RetrievalProgressContextValue {
  const ctx = useContext(RetrievalProgressContext);
  if (!ctx) {
    throw new Error('useRetrievalProgressContext must be used within RetrievalProgressProvider');
  }
  return ctx;
}
